import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { ModSortBy } from '../../../constants/enums';
import { Mod, ModsQueryParams } from '../types/modTypes';
import { sortMods } from '../utils/modSortUtils';
import mockModsData from './mockMods.json';

const cloneMockMods = (): Mod[] => JSON.parse(JSON.stringify(mockModsData)) as Mod[];

// `addMod`/`updateMod` mutate this in-memory array directly to simulate a
// backend without one. It's cloned from the bundled JSON (never mutates the
// imported module itself) but is still a single array shared by every caller
// for the lifetime of the module — resettable via `resetMockModsForTests` so
// test files that exercise the mutations don't leak state between test cases.
let MOCK_MODS: Mod[] = cloneMockMods();

/** Test-only: restores the in-memory mock "database" to its initial snapshot. */
export const resetMockModsForTests = (): void => {
  MOCK_MODS = cloneMockMods();
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getModsProvidesTags = (result: Mod[] | undefined) =>
  result
    ? [
        ...result.map(({ id }) => ({ type: 'Mod' as const, id })),
        { type: 'Mod' as const, id: 'LIST' },
      ]
    : [{ type: 'Mod' as const, id: 'LIST' }];

export const modsApi = createApi({
  reducerPath: 'modsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Mod'],
  endpoints: (builder) => ({
    getMods: builder.query<Mod[], ModsQueryParams | void>({
      queryFn: async (params) => {
        await delay(300);

        let filtered = [...MOCK_MODS];
        const category = params?.category;

        if (category) {
          filtered = filtered.filter((m) => m.category === category);
        }

        const sortBy = params?.sortBy || ModSortBy.UPDATED_AT;
        filtered = sortMods(filtered, sortBy);

        return { data: filtered };
      },
      providesTags: getModsProvidesTags,
    }),
    getModById: builder.query<Mod | null, string>({
      queryFn: async (id) => {
        await delay(150);
        const mod = MOCK_MODS.find((m) => m.id === id) || null;
        return { data: mod };
      },
      providesTags: (result, error, id) => [{ type: 'Mod' as const, id }],
    }),
    addMod: builder.mutation<Mod, Omit<Mod, 'id'> & { id?: string }>({
      queryFn: async (newModData) => {
        await delay(150);
        const now = new Date().toISOString();
        const newMod: Mod = {
          createdAt: newModData.createdAt || now,
          updatedAt: newModData.updatedAt || now,
          createDate: newModData.createDate || now,
          updateDate: newModData.updateDate || now,
          ...newModData,
          id: newModData.id || `mod-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        };
        MOCK_MODS.unshift(newMod);
        return { data: newMod };
      },
      invalidatesTags: [{ type: 'Mod' as const, id: 'LIST' }],
    }),
    updateMod: builder.mutation<Mod, Mod>({
      queryFn: async (updatedMod) => {
        await delay(150);
        const index = MOCK_MODS.findIndex((m) => m.id === updatedMod.id);
        if (index !== -1) {
          MOCK_MODS[index] = { ...MOCK_MODS[index], ...updatedMod };
        } else {
          MOCK_MODS.unshift({ ...updatedMod });
        }
        return { data: updatedMod };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Mod' as const, id: arg.id },
        { type: 'Mod' as const, id: 'LIST' },
      ],
    }),
  }),
});

export const { useGetModsQuery, useGetModByIdQuery, useAddModMutation, useUpdateModMutation } =
  modsApi;
